import { useEffect } from 'react';

// Browser-tab icon: the GLOS ring mark (the sidebar's GlosMark), resting and then
// making one full turn every 8s. Chrome never animates an SVG favicon (it shows
// the first frame), so the ring is redrawn on a canvas and the tab icon swapped
// ~20 times a second while it turns. public/favicon.svg stays as the static icon
// before JS loads and for people who prefer reduced motion.

const SIZE = 64;
const FPS = 20;
// Geometry in a 100-unit box, matching public/favicon.svg.
const RING_R = 36;
const RING_W = 20;
const GAP_DEG = 9; // the straight cuts on the axes, like the wordmark's O
// Rest, then one turn in the last 1.8s of every 8s; first turn ~2s after load.
const ROUND_MS = 8000;
const TURN_DUR = 1800;
const TURN_START = ROUND_MS - TURN_DUR;
const OFFSET_MS = 4000;

const clamp01 = (x) => Math.min(1, Math.max(0, x));
// ≈ cubic-bezier(0.65, 0, 0.35, 1)
const easeInOut = (x) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);
const rad = (deg) => (deg * Math.PI) / 180;

const roundTime = (elapsed) => (elapsed + OFFSET_MS) % ROUND_MS;
const ringAngle = (t) => easeInOut(clamp01((t - TURN_START) / TURN_DUR)) * 2 * Math.PI;
// Between turns the icon doesn't change, so it isn't redrawn (small margin so
// the end of each turn is drawn).
const isTurning = (t) => t >= TURN_START - 100 || t < 100;

function draw(ctx, t) {
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, SIZE, SIZE);
  const k = SIZE / 100;
  ctx.setTransform(k, 0, 0, k, 0, 0);
  ctx.translate(50, 50);
  ctx.rotate(ringAngle(t));

  const grad = ctx.createLinearGradient(-RING_R, -RING_R, RING_R, RING_R);
  grad.addColorStop(0, '#1739d6');
  grad.addColorStop(0.55, '#0ab0e0');
  grad.addColorStop(1, '#3fcf6a');
  ctx.strokeStyle = grad;
  ctx.lineWidth = RING_W;
  ctx.lineCap = 'butt';
  for (let q = 0; q < 4; q++) {
    ctx.beginPath();
    ctx.arc(0, 0, RING_R, rad(q * 90 + GAP_DEG / 2), rad((q + 1) * 90 - GAP_DEG / 2));
    ctx.stroke();
  }
}

export default function AnimatedFavicon() {
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    // Browsers prefer the static SVG icon over a PNG, so take it out of the
    // running while animating and put it back on unmount.
    const staticIcons = [...document.querySelectorAll('link[rel~="icon"]')];
    staticIcons.forEach((l) => l.setAttribute('rel', 'glos-static-icon'));

    const link = document.createElement('link');
    link.rel = 'icon';
    link.type = 'image/png';
    document.head.appendChild(link);

    const start = performance.now();
    let drewRest = false;
    const tick = () => {
      const t = roundTime(performance.now() - start);
      if (!isTurning(t)) {
        if (drewRest) return; // ring at rest: the last frame is still right
        drewRest = true;
      } else {
        drewRest = false;
      }
      draw(ctx, t);
      link.href = canvas.toDataURL('image/png');
    };
    tick();
    const id = window.setInterval(tick, 1000 / FPS);
    return () => {
      window.clearInterval(id);
      link.remove();
      staticIcons.forEach((l) => l.setAttribute('rel', 'icon'));
    };
  }, []);

  return null;
}
